import type { SerializedDockview } from 'dockview';
import { DEFAULT_LAYOUT_ID } from '../../../components/AppLayout/Layouts/tokens';
import { BLOTTER_PANELS } from '../../../components/AppLayout/Layouts/Trading/shared';
import { addPanelToLayout } from '../../../components/AppLayout/utils/addPanelToLayout';
import type { PanelType, TypedGroupviewPanelState } from '../../../types/LayoutConfig';
import {
  PREVIOUSLY_REQUIRED_TRADING_PANELS_MIGRATION_VERSION,
  shouldRunRequiredPanelsMigration,
} from '../requiredPanelsMigrationVersion';

/**
 * Panel types that used to be injected whenever the user had the feature enabled; layouts were
 * stripped when the user lacked permission. After those panels became optional in the layout,
 * restore them once so they can stay in the layout and be hidden by permissions instead of removed.
 */
export const PREVIOUSLY_REQUIRED_TRADING_PANEL_TYPES = [
  'reconMismatches',
  'allocations',
  'hedgePositionStatus',
  'otcDerivatives',
  'loans',
  'repos',
  'creditBlotter',
  'balancesBlotter',
  'transfersBlotter',
] as const satisfies readonly PanelType[];

function blotterPanelDefForContentType(panelType: PanelType): TypedGroupviewPanelState | undefined {
  return Object.values(BLOTTER_PANELS).find(p => p.contentComponent === panelType);
}

function panelTypeExistsInLayout(layout: SerializedDockview, panelType: PanelType): boolean {
  return Object.values(layout.panels).some(p => p.contentComponent === panelType);
}

export function addRequiredBlotterPanels(layout: SerializedDockview): SerializedDockview {
  if (layout.grid?.root == null) {
    return layout;
  }

  let updated = layout;
  for (const panelType of PREVIOUSLY_REQUIRED_TRADING_PANEL_TYPES) {
    if (panelTypeExistsInLayout(updated, panelType)) {
      continue;
    }
    const panelDef = blotterPanelDefForContentType(panelType);
    if (panelDef == null) {
      continue;
    }
    updated = addPanelToLayout({
      layout: updated,
      addedPanel: panelDef,
      groupId: 'blotters',
      panelId: 'orders',
    });
  }
  return updated;
}

/** [UI-7372] One-time restore of formerly required blotter panels on the default trading layout only. */
export function migrateRequiredPanels(replacedConfig: Record<string, any>) {
  if (!shouldRunRequiredPanelsMigration(replacedConfig)) {
    return;
  }

  const trading = replacedConfig.layoutConfigs?.trading;
  if (trading != null && typeof trading === 'object') {
    const spec = trading[DEFAULT_LAYOUT_ID];
    const dockViewLayout = spec?.dockViewLayout as SerializedDockview | undefined;
    if (dockViewLayout != null) {
      spec.dockViewLayout = addRequiredBlotterPanels(dockViewLayout);
    }
  }

  replacedConfig.requiredPanelsMigrationVersion = PREVIOUSLY_REQUIRED_TRADING_PANELS_MIGRATION_VERSION;
}
